import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Group, Modal, SegmentedControl, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  ApiError,
  allocateReservation,
  fetchResourceOccupancy,
  suggestAvailability,
  type CandidateSegment,
  type DashboardRow,
  type OccupancyView,
  type Zone,
} from '../api-client.ts';
import { SeatMap } from './SeatMap.tsx';
import type { ZoneWithOccupancy } from '../hooks/useDashboard.ts';
import { tokens } from '../ui/tokens.ts';

interface Props {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly onAllocated: () => void;
  readonly reservation: DashboardRow | null;
  readonly venueId: string;
  readonly zones: readonly Zone[];
}

export function AllocateSeatsModal(props: Props) {
  const { opened, onClose, onAllocated, reservation, zones } = props;

  const [selectedZoneId, setSelectedZoneId] = useState<string>('');
  const [candidates, setCandidates] = useState<CandidateSegment[]>([]);
  const [occupancy, setOccupancy] = useState<OccupancyView | null>(null);
  const [selected, setSelected] = useState<CandidateSegment | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedZone = useMemo<Zone | null>(
    () => zones.find((z) => z.resourceId === selectedZoneId) ?? zones[0] ?? null,
    [zones, selectedZoneId],
  );

  useEffect(() => {
    if (opened && zones.length > 0 && !selectedZoneId) {
      setSelectedZoneId(zones[0]!.resourceId);
    }
  }, [opened, zones, selectedZoneId]);

  const refresh = useCallback(async () => {
    if (!reservation || !selectedZone) return;
    setLoading(true);
    setSelected(null);
    try {
      const [suggest, occ] = await Promise.all([
        suggestAvailability({
          venueId: props.venueId,
          serviceId: selectedZone.serviceId,
          scenarioType: selectedZone.scenarioType,
          resourceId: selectedZone.resourceId,
          partySize: reservation.partySize,
          startTime: reservation.startTime,
          endTime: reservation.endTime,
        }),
        fetchResourceOccupancy({
          resourceId: selectedZone.resourceId,
          startTime: reservation.startTime,
          endTime: reservation.endTime,
        }),
      ]);
      setCandidates(suggest.candidateSegments);
      setOccupancy(occ);
      if (suggest.candidateSegments.length === 0) {
        notifications.show({
          color: 'yellow',
          title: '無可用座位',
          message: '該時段沒有符合容量需求的連續座位段',
        });
      } else {
        setSelected(suggest.candidateSegments[0] ?? null);
      }
    } catch (e) {
      notifications.show({
        color: 'red',
        title: '取得候選失敗',
        message: e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e),
      });
    } finally {
      setLoading(false);
    }
  }, [reservation, selectedZone, props.venueId]);

  useEffect(() => {
    if (opened) void refresh();
  }, [opened, refresh]);

  async function confirm() {
    if (!reservation || !selected || !selectedZone) return;
    setSubmitting(true);
    try {
      await allocateReservation(reservation.reservationId, {
        resourceId: selectedZone.resourceId,
        segment: { startPosition: selected.startPosition, endPosition: selected.endPosition },
      });
      notifications.show({
        color: 'green',
        title: '座位已分配',
        message: `${selectedZone.zoneLabel} · 位置 ${selected.startPosition}–${selected.endPosition}`,
      });
      onAllocated();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        notifications.show({
          color: 'red',
          title: '座位已被其他人預訂',
          message: '正在重新取得候選座位',
        });
        await refresh();
      } else {
        notifications.show({
          color: 'red',
          title: '分配失敗',
          message: e instanceof ApiError ? `[${e.code}] ${e.message}` : String(e),
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  const zoneView: ZoneWithOccupancy[] = selectedZone
    ? [{ zone: selectedZone, occupancy }]
    : [];

  return (
    <Modal opened={opened} onClose={onClose} title="分配座位" size="lg" centered>
      <Stack gap="sm">
        {reservation && (
          <Group gap="xs">
            <Badge color={tokens.color.mantine['neutral']} variant="light">
              {reservation.partySize} 人
            </Badge>
            <Text size="sm" c="dimmed">
              {reservation.startTime.slice(11, 16)} UTC ~ {reservation.endTime.slice(11, 16)} UTC
            </Text>
          </Group>
        )}
        {zones.length > 1 && selectedZone && (
          <SegmentedControl
            value={selectedZone.resourceId}
            onChange={setSelectedZoneId}
            data={zones.map((z) => ({ value: z.resourceId, label: z.zoneLabel }))}
            color={tokens.color.mantine['primary']}
            fullWidth
          />
        )}
        <SeatMap
          zones={zoneView}
          highlight={
            selected
              ? { startPosition: selected.startPosition, endPosition: selected.endPosition }
              : null
          }
          loading={loading}
        />
        <Text fw={600} size="sm" mt="xs">
          候選座位段（{candidates.length}）
        </Text>
        <Stack gap="xs">
          {candidates.map((c, idx) => {
            const isSelected =
              selected?.startPosition === c.startPosition && selected.endPosition === c.endPosition;
            return (
              <Card
                key={`${c.startPosition}-${c.endPosition}`}
                withBorder
                padding="xs"
                radius="sm"
                style={{
                  cursor: 'pointer',
                  borderColor: isSelected ? tokens.color.primary : undefined,
                  background: isSelected ? tokens.color.pageBackground : undefined,
                }}
                onClick={() => setSelected(c)}
              >
                <Group justify="space-between">
                  <Text size="sm" fw={500}>
                    #{idx + 1} · 位置 {c.startPosition} – {c.endPosition}
                  </Text>
                  <Text size="xs" c="dimmed">
                    容量 {c.totalCapacity} · 緩衝 {c.bufferCapacity} · 分數 {c.score.toFixed(2)}
                  </Text>
                </Group>
              </Card>
            );
          })}
        </Stack>
        <Group justify="flex-end" mt="sm">
          <Button variant="light" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={() => void confirm()}
            loading={submitting}
            disabled={!selected || loading}
          >
            確認分配
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
